'use client';

import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import Link from 'next/link';
import StaffModal from '@/components/StaffModal';
import { useStaff } from '@/lib/swr/hooks/useStaff';
import { attachFooterIntent, type FooterState } from './footerIntent';
import styles from './home-lab.module.css';

function StaffDirectory({ onClose }: { onClose: () => void }) {
  const { staff, isLoading, error, mutate } = useStaff(20);
  const dialog = useRef<HTMLDivElement>(null);
  const pending = isLoading || Boolean(error);
  useEffect(() => {
    dialog.current?.querySelector<HTMLButtonElement>('button')?.focus();
  }, [pending]);
  useEffect(() => {
    if (!pending) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previous; };
  }, [pending]);
  useEffect(() => {
    const onKey = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); onClose(); }
      if (event.key !== 'Tab') return;
      const items = dialog.current?.querySelectorAll<HTMLElement>('a[href], button:not(:disabled)');
      if (!items?.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);
  return createPortal(
    <div ref={dialog} className={pending ? styles.staffOverlay : undefined} role="dialog" aria-modal="true" aria-label="Сотрудники лаборатории">
      {pending ? <div className={styles.staffNotice}>
        <p role="status">{error ? 'Не удалось загрузить сотрудников.' : 'Загрузка сотрудников…'}</p>
        {error && <button type="button" onClick={() => { void mutate().catch(() => undefined); }}>Повторить</button>}
        <button type="button" onClick={onClose}>Закрыть</button>
      </div> : <StaffModal open onClose={onClose} staff={staff} />}
    </div>
  , document.body);
}

export default function HomeLabFooter({ railRef }: { railRef: RefObject<HTMLDivElement | null> }) {
  const [state, setState] = useState<FooterState>({ atBottom: false, visible: false });
  const [staffOpen, setStaffOpen] = useState(false);
  const controller = useRef<ReturnType<typeof attachFooterIntent> | null>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const staffTrigger = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLElement>(null);
  const focusFrame = useRef(0);
  useEffect(() => {
    if (!railRef.current) return;
    const api = attachFooterIntent(railRef.current, next => {
      setState(next);
      if (!next.visible && panel.current?.contains(document.activeElement)) {
        railRef.current?.querySelector<HTMLButtonElement>('button[data-chapter-index="4"]')?.focus({ preventScroll: true });
      }
    });
    controller.current = api;
    const observer = new ResizeObserver(api.refresh);
    observer.observe(railRef.current);
    return () => { api.dispose(); observer.disconnect(); cancelAnimationFrame(focusFrame.current); controller.current = null; };
  }, [railRef]);
  const reveal = () => {
    controller.current?.open();
    // Кнопка исчезает после открытия; переводим её клавиатурный фокус в панель.
    cancelAnimationFrame(focusFrame.current);
    focusFrame.current = requestAnimationFrame(() => panel.current?.querySelector<HTMLAnchorElement>('a')?.focus({ preventScroll: true }));
  };
  const dismiss = () => {
    controller.current?.close();
    cancelAnimationFrame(focusFrame.current);
    focusFrame.current = requestAnimationFrame(() => trigger.current?.focus({ preventScroll: true }));
  };
  const closeStaff = useCallback(() => {
    setStaffOpen(false);
    staffTrigger.current?.focus({ preventScroll: true });
  }, []);
  return (
    <>
      {state.atBottom && !state.visible && <button ref={trigger} type="button" className={styles.footerTrigger} aria-expanded={false} aria-controls="lab-footer" onClick={reveal}>Контакты и команда</button>}
      <footer ref={panel} id="lab-footer" className={styles.floatingFooter} hidden={!state.visible} aria-label="Ссылки лаборатории">
        <small>© {new Date().getFullYear()}<span className={styles.footerBrand}> ЛИСАД · КБГУ</span></small>
        <nav aria-label="О лаборатории">
          <Link href="/about#contact" prefetch={false}>Контакты</Link>
          <Link href="/about#lab" prefetch={false} className={styles.footerLabLink}>Лаборатория</Link>
          <button ref={staffTrigger} type="button" onClick={() => setStaffOpen(true)}>Сотрудники</button>
          <Link href="/messages?support=1" prefetch={false}>Связаться</Link>
        </nav>
        <button type="button" className={styles.footerClose} aria-label="Скрыть ссылки лаборатории" onClick={dismiss}>×</button>
      </footer>
      {staffOpen && <StaffDirectory onClose={closeStaff} />}
    </>
  );
}
